import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { StoreCartShippingOption } from "@medusajs/types";
import { useEffect } from "react";
import {
  addShippingMethodMutationKey,
  type AddCartShippingMethodInput
} from "./mutations";

/**
 * Finds the "Standard" shipping option among the available options, if any.
 * Matches by name (case-insensitive substring) since option names/ids vary by seller.
 */
function findStandardOption(
  options: StoreCartShippingOption[]
): StoreCartShippingOption | undefined {
  return options.find((opt) => opt.name?.toLowerCase().includes("standard"));
}

/**
 * The option list each cart last had an auto-select attempted for, per query
 * client. One attempt per list, not one per cart (KAN-230): an address change
 * can make the server drop the method it auto-picked from the previous list (a
 * baseline estimate that the live rates replace), and a once-per-cart guard
 * then left the cart with no method at all. A new list re-arms it; the same
 * list never fires twice, so a POST that fails or that the server drops cannot
 * loop.
 *
 * Held per client rather than per hook instance because several mounted
 * consumers (the app-shell watcher, the Cart view's seller cards, checkout's
 * shipping rows) can all see the same method-less cart in the same commit.
 * Each keeping its own guard would POST once each; a shared one lets the
 * first claim it and the rest stand down (KAN-267).
 */
const attemptedListsByClient = new WeakMap<QueryClient, Map<string, string>>();

function attemptedListsFor(client: QueryClient): Map<string, string> {
  let attempted = attemptedListsByClient.get(client);
  if (!attempted) {
    attempted = new Map();
    attemptedListsByClient.set(client, attempted);
  }
  return attempted;
}

/**
 * Re-arms the auto-select for a cart when a shipping selector mounts for it.
 *
 * The attempt guard outlives any one component so the app-shell watcher cannot
 * loop on a POST that fails (it remounts on every rollback). The cost is that a
 * failed attempt would never be retried; mounting a selector - opening the Cart
 * tab or checkout - is the buyer's deliberate look at shipping, so it is the
 * point at which one more attempt is owed (KAN-267).
 */
export function useRearmShippingAutoSelect(
  cartId: string,
  enabled: boolean
): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (enabled) {
      attemptedListsFor(queryClient).delete(cartId);
    }
  }, [queryClient, cartId, enabled]);
}

export interface StandardShippingAutoSelectState {
  cartId: string;
  /** The id in `listedOptions` the cart's method is selected as, if any. */
  selectedOptionId?: string;
  isSelectionValid: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isAddingShippingMethod: boolean;
  listedOptions: StoreCartShippingOption[];
  addShippingMethod: (input: AddCartShippingMethodInput) => void;
}

/**
 * Auto-selects "Standard" shipping when a cart has no valid method for the list
 * it is offered. Never overrides a valid customer selection, and never picks
 * from a list that is still being refetched - after an address change that list
 * is the old address's, and its option may be the very one the server is about
 * to drop.
 *
 * Deliberately a bare effect over state the caller already has, so
 * `useShippingMethodSync` (which owns that state for the selector UI) can
 * hand it over, and so the Cart view, checkout and the app-shell watcher (which
 * mounts it for carts nobody is rendering a selector for) all run exactly the
 * same rule.
 */
export function useStandardShippingAutoSelect({
  cartId,
  selectedOptionId,
  isSelectionValid,
  isLoading,
  isFetching,
  isAddingShippingMethod,
  listedOptions,
  addShippingMethod
}: StandardShippingAutoSelectState): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      (selectedOptionId && isSelectionValid) ||
      isLoading ||
      isFetching ||
      isAddingShippingMethod ||
      listedOptions.length === 0
    ) {
      return;
    }

    // A POST for this cart is already out (another consumer's, or ours before
    // its pending flag reached this render) - leave it to land.
    if (
      queryClient.isMutating({
        mutationKey: addShippingMethodMutationKey(cartId)
      }) > 0
    ) {
      return;
    }

    const attempted = attemptedListsFor(queryClient);
    const listKey = listedOptions.map((opt) => opt.id).join(",");
    if (attempted.get(cartId) === listKey) {
      return;
    }

    const standardOption = findStandardOption(listedOptions);
    if (standardOption) {
      attempted.set(cartId, listKey);
      addShippingMethod({
        optionId: standardOption.id,
        name: standardOption.name,
        amount: standardOption.amount ?? 0
      });
    }
  }, [
    queryClient,
    cartId,
    selectedOptionId,
    isSelectionValid,
    isLoading,
    isFetching,
    isAddingShippingMethod,
    listedOptions,
    addShippingMethod
  ]);
}
